import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { SettingsIcon, CalendarIcon, DollarSignIcon, MapIcon, ShieldAlertIcon, FileTextIcon, XIcon } from 'lucide-react';
export function SystemConfiguration() {
  const [activeTab, setActiveTab] = useState<'ban' | 'tariff' | 'geo' | 'fine'>('ban');
  // Propose Rate Change Modal
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateForm, setRateForm] = useState({
    vesselType: '',
    registeredFee: '',
    unregisteredFee: ''
  });
  // Fine Policy Data
  const [entryFees, setEntryFees] = useState([{
    id: 1,
    category: 'Entry / Passage Fee',
    vesselType: 'Launch (over 100 feet)',
    registeredFee: '1,500.00',
    unregisteredFee: '6,000.00'
  }, {
    id: 2,
    category: 'Entry / Passage Fee',
    vesselType: 'Launch (50 to 100 feet)',
    registeredFee: '1,200.00',
    unregisteredFee: '8,500.00'
  }, {
    id: 3,
    category: 'Entry / Passage Fee',
    vesselType: 'Launch (under 50 feet)',
    registeredFee: '950.00',
    unregisteredFee: '5,000.00'
  }, {
    id: 4,
    category: 'Entry / Passage Fee',
    vesselType: 'Trawler / Sampan',
    registeredFee: '500.00',
    unregisteredFee: '1,500.00'
  }, {
    id: 5,
    category: 'Entry / Passage Fee',
    vesselType: 'Country Boat (Deshi Nouka)',
    registeredFee: '150.00',
    unregisteredFee: '500.00'
  }, {
    id: 6,
    category: 'Entry / Passage Fee',
    vesselType: 'Speedboat',
    registeredFee: '3,000.00',
    unregisteredFee: '6,000.00'
  }, {
    id: 7,
    category: 'Entry / Passage Fee',
    vesselType: 'Speedboat (with Motor Vessel)',
    registeredFee: '1,000.00',
    unregisteredFee: '2,000.00'
  }, {
    id: 8,
    category: 'Entry / Passage Fee',
    vesselType: 'Tourist Boat (Jali Boat)',
    registeredFee: '350.00',
    unregisteredFee: '1,500.00'
  }, {
    id: 9,
    category: 'Entry / Passage Fee',
    vesselType: 'Helicopter / Seaplane (per trip)',
    registeredFee: '2,500.00',
    unregisteredFee: '6,000.00'
  }, {
    id: 10,
    category: 'Stay Fee (per day)',
    vesselType: 'Per Deer / Animal',
    registeredFee: '600.00',
    unregisteredFee: '3,000.00'
  }, {
    id: 11,
    category: 'Stay Fee (per day)',
    vesselType: 'Trawler, Speedboat, Country Boat (with Motor Vessel)',
    registeredFee: '300.00',
    unregisteredFee: '900.00'
  }]);
  const fineColumns = [{
    key: 'category',
    label: 'Fee Category'
  }, {
    key: 'vesselType',
    label: 'Vessel / Vehicle Type'
  }, {
    key: 'registeredFee',
    label: 'Registered (৳)',
    render: (val: string) => <span className="font-mono font-medium text-green-700" data-id="element-2664" data-mp="gp014cmeut98">৳ {val}</span>
  }, {
    key: 'unregisteredFee',
    label: 'Unregistered (৳)',
    render: (val: string) => <span className="font-mono font-medium text-red-700" data-id="element-2665" data-mp="2584cvbwamma5">৳ {val}</span>
  }];
  // Mock Data
  const banCalendar = [{
    resource: 'Honey',
    startDate: '2024-06-01',
    endDate: '2024-07-31',
    status: 'Active',
    approvedBy: 'Admin'
  }, {
    resource: 'Fish',
    startDate: '2024-05-15',
    endDate: '2024-08-15',
    status: 'Pending Approval',
    approvedBy: '-'
  }, {
    resource: 'Wood',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    status: 'Active',
    approvedBy: 'Admin'
  }];
  const tariffTable = [{
    resource: 'Honey',
    unit: 'Kg',
    baseFee: '50',
    tax: '15%',
    status: 'Active'
  }, {
    resource: 'Fish',
    unit: 'Kg',
    baseFee: '20',
    tax: '10%',
    status: 'Active'
  }, {
    resource: 'Golpata',
    unit: 'Maund',
    baseFee: '100',
    tax: '15%',
    status: 'Pending Approval'
  }];
  const geoHierarchy = [{
    level: 'Division',
    name: 'Khulna',
    parent: '-'
  }, {
    level: 'Range',
    name: 'Chandpai',
    parent: 'Khulna'
  }, {
    level: 'Station',
    name: 'Karamjal',
    parent: 'Chandpai'
  }, {
    level: 'Compartment',
    name: 'C-12',
    parent: 'Karamjal'
  }];
  const banColumns = [{
    key: 'resource',
    label: 'Resource'
  }, {
    key: 'startDate',
    label: 'Start Date'
  }, {
    key: 'endDate',
    label: 'End Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (val: string) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'Active' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`} data-id="element-2666" data-mp="31brw187njy0i">
          {val}
        </span>
  }, {
    key: 'approvedBy',
    label: 'Approved By'
  }];
  const tariffColumns = [{
    key: 'resource',
    label: 'Resource'
  }, {
    key: 'unit',
    label: 'Unit'
  }, {
    key: 'baseFee',
    label: 'Base Fee (৳)'
  }, {
    key: 'tax',
    label: 'Tax (%)'
  }, {
    key: 'status',
    label: 'Status',
    render: (val: string) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'Active' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`} data-id="element-2667" data-mp="1n5n75j8qwmqf">
          {val}
        </span>
  }];
  const geoColumns = [{
    key: 'level',
    label: 'Level'
  }, {
    key: 'name',
    label: 'Name'
  }, {
    key: 'parent',
    label: 'Parent Node'
  }];
  const handleProposeRate = () => {
    if (!rateForm.vesselType || !rateForm.registeredFee || !rateForm.unregisteredFee) return;
    const newEntry = {
      id: entryFees.length + 1,
      category: 'Entry / Passage Fee',
      vesselType: rateForm.vesselType,
      registeredFee: parseFloat(rateForm.registeredFee).toLocaleString('en-US', {
        minimumFractionDigits: 2
      }),
      unregisteredFee: parseFloat(rateForm.unregisteredFee).toLocaleString('en-US', {
        minimumFractionDigits: 2
      })
    };
    setEntryFees([...entryFees, newEntry]);
    setRateForm({
      vesselType: '',
      registeredFee: '',
      unregisteredFee: ''
    });
    setShowRateModal(false);
  };
  return <div className="space-y-6" data-id="element-2668" data-mp="xth26kbjz289">
      <div data-id="element-2669" data-mp="12sk8s976lgic">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-2670" data-mp="12rsb8su0x8pf">
          <SettingsIcon className="text-[#1B5E20]" data-id="element-2671" data-mp="17j0oy12dzg8e" />
          System Configuration
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2672" data-mp="a3mq44z25hp2">
          Global settings, ban periods, tariffs, fine policy, and geographical
          hierarchy
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2673" data-mp="f0mwyi3qeoh8">
        <ShieldAlertIcon className="text-blue-600 mt-0.5" size={20} data-id="element-2674" data-mp="7if95n9vauo5" />
        <div data-id="element-2675" data-mp="2lf5fj0hq407">
          <h4 className="font-semibold text-blue-900" data-id="element-2676" data-mp="2kn7w2nc1w7a">
            Maker-Checker Policy Active
          </h4>
          <p className="text-sm text-blue-700 mt-1" data-id="element-2677" data-mp="3tolqlkioakl1">
            Any changes to Ban Dates or Tariffs require Admin/Financial Admin
            approval before taking effect across stations.
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-2678" data-mp="3fz0e8szdp3lz">
        <div className="flex gap-4" data-id="element-2679" data-mp="3fy8jfkdlv9wr">
          <button onClick={() => setActiveTab('ban')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'ban' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2680" data-mp="3fx73b1hqfx86">
            <CalendarIcon size={16} data-id="element-2681" data-mp="2x05wrc8j5p24" />
            Ban Calendar
          </button>
          <button onClick={() => setActiveTab('tariff')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tariff' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2682" data-mp="2x28w4mvkouji">
            <DollarSignIcon size={16} data-id="element-2683" data-mp="ft47ruo8h3yu" />
            Tariff Master
          </button>
          <button onClick={() => setActiveTab('fine')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'fine' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2684" data-mp="jug2ufpvmhef">
            <FileTextIcon size={16} data-id="element-2685" data-mp="3s63m6dprtpyt" />
            Fine Policy
          </button>
          <button onClick={() => setActiveTab('geo')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'geo' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2686" data-mp="12eywyy8w0y5">
            <MapIcon size={16} data-id="element-2687" data-mp="34l1m2n111t9p" />
            Geo Hierarchy
          </button>
        </div>
      </div>

      {activeTab === 'ban' && <div className="space-y-4" data-id="element-2688" data-mp="3dl7uhuhvc320">
          <div className="flex justify-end" data-id="element-2689" data-mp="3dmj1022p7owt">
            <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2690" data-mp="3dj4wan6kb3m7">
              + Propose Ban Update
            </button>
          </div>
          <DataTable columns={banColumns} data={banCalendar} searchPlaceholder="Search resources..." data-id="element-2691" data-mp="3nhkl2ji4a0sj" />
        </div>}

      {activeTab === 'tariff' && <div className="space-y-4" data-id="element-2692" data-mp="15vtum3jikj6">
          <div className="flex justify-end" data-id="element-2693" data-mp="3sa3ztjsjj1yx">
            <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2694" data-mp="3sb5lei76n6uw">
              + Propose Tariff Change
            </button>
          </div>
          <DataTable columns={tariffColumns} data={tariffTable} searchPlaceholder="Search resources..." data-id="element-2695" data-mp="578xcpj5ubmx" />
        </div>}

      {activeTab === 'geo' && <div className="space-y-4" data-id="element-2696" data-mp="2sw6xtg54kp8k">
          <div className="flex justify-end" data-id="element-2697" data-mp="2ny5dyszvklw1">
            <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2698" data-mp="2nywyn8rxoz7j">
              + Add Node
            </button>
          </div>
          <DataTable columns={geoColumns} data={geoHierarchy} searchPlaceholder="Search locations..." data-id="element-2699" data-mp="3gm18ps5vw3q6" />
        </div>}

      {activeTab === 'fine' && <div className="space-y-4" data-id="element-2700" data-mp="36nv5964fipj7">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2701" data-mp="36qzjkwblz1nl">
            <ShieldAlertIcon className="text-amber-600 mt-0.5" size={20} data-id="element-2702" data-mp="31s65b1ohz6md" />
            <div data-id="element-2703" data-mp="1vbaw83ug00m9">
              <h4 className="font-semibold text-amber-900" data-id="element-2704" data-mp="1v7n2p4ozze7j">
                Fine & Fee Policy — Vessel Based Rates
              </h4>
              <p className="text-sm text-amber-700 mt-1" data-id="element-2705" data-mp="5t4a8fbzkp6l">
                Fee amounts are determined by vessel type and registration
                status. Registered vessels receive reduced rates. All amounts
                are in BDT (৳).
              </p>
            </div>
          </div>

          <div className="flex justify-end" data-id="element-2706" data-mp="kh8kqw0vxuea">
            <button onClick={() => setShowRateModal(true)} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2707" data-mp="kj35a988vkhu">
              + Propose Rate Change
            </button>
          </div>

          <DataTable columns={fineColumns} data={entryFees} onDataChange={setEntryFees} searchPlaceholder="Search vessel types..." data-id="element-2708" data-mp="yh05dflwq919" />
        </div>}

      {/* Propose Rate Change Modal */}
      {showRateModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2709" data-mp="357he0fs15mg9">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6" data-id="element-2710" data-mp="2x71q2bjcnxyi">
            <div className="flex items-center justify-between mb-6" data-id="element-2711" data-mp="2x8uvxom52e5g">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-2712" data-mp="2t70xzo3bvo8z">
                Propose New Rate
              </h3>
              <button onClick={() => setShowRateModal(false)} className="p-1 hover:bg-gray-100 rounded-lg" data-id="element-2713" data-mp="376ipib80dfm1">
                <XIcon size={20} className="text-gray-500" data-id="element-2714" data-mp="3g54640tx6kk6" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-2715" data-mp="3c53sgienade6">
              <div data-id="element-2716" data-mp="3c4281nbqoejs">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2717" data-mp="3pvpegfzeb3ga">
                  Vessel Type *
                </label>
                <select value={rateForm.vesselType} onChange={e => setRateForm({
              ...rateForm,
              vesselType: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2718" data-mp="3kwcjf7ydeq2m">
                  <option value="" data-id="element-2719" data-mp="mc9f3bevxtf7">Select Vessel Type</option>
                  <option value="Boat" data-id="element-2720" data-mp="8djhluo5lrqb">Boat</option>
                  <option value="Launch" data-id="element-2721" data-mp="8cs01nre5uj1">Launch</option>
                  <option value="Trawler" data-id="element-2722" data-mp="dbbmdp9wd8bx">Trawler</option>
                  <option value="Rescue Boat" data-id="element-2723" data-mp="ddxr7h5el9qg">Rescue Boat</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2724" data-mp="3dx3zk4ey9gz">
                <div data-id="element-2725" data-mp="3eqcoqmxn91r">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2726" data-mp="3g1hmts46mug">
                    Registered Fee (৳) *
                  </label>
                  <input type="number" value={rateForm.registeredFee} onChange={e => setRateForm({
                ...rateForm,
                registeredFee: e.target.value
              })} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2727" data-mp="3qq7xsvqhv9n8" />
                </div>
                <div data-id="element-2728" data-mp="e6p76c643p69">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2729" data-mp="e671yq1h20m7">
                    Unregistered Fee (৳) *
                  </label>
                  <input type="number" value={rateForm.unregisteredFee} onChange={e => setRateForm({
                ...rateForm,
                unregisteredFee: e.target.value
              })} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2730" data-mp="4b5c1rzdguik" />
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-2731" data-mp="1s8ei8nbfg2qn">
                <p className="text-xs text-blue-700" data-id="element-2732" data-mp="1s6afi8p9l9ry">
                  <strong data-id="element-2733" data-mp="1x5wv2oskf23x">Note:</strong> Proposed rate changes require
                  Admin/Financial Admin approval before taking effect.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-2734" data-mp="26vzo2ozosg29">
              <button onClick={() => setShowRateModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-2735" data-mp="199af1v6kh36w">
                Cancel
              </button>
              <button onClick={handleProposeRate} disabled={!rateForm.vesselType || !rateForm.registeredFee || !rateForm.unregisteredFee} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2736" data-mp="1j658hznzgdeb">
                Submit Proposal
              </button>
            </div>
          </div>
        </div>}
    </div>;
}